import type { IBaseAttributes, IRachisMessage, TConstraints } from '@feathr/rachis';
import { Collection, isWretchError, Model, wretch } from '@feathr/rachis';

export enum EGrowthCreditEntryClass {
  Base = 'GrowthCreditEntry',
  Redemption = 'GrowthCreditEntry.RedemptionGrowthCreditEntry',
  MediaRedemption = 'GrowthCreditEntry.RedemptionGrowthCreditEntry.MediaRedemptionGrowthCreditEntry',
  InvoiceRedemption = 'GrowthCreditEntry.RedemptionGrowthCreditEntry.InvoiceRedemptionGrowthCreditEntry',
  Manual = 'GrowthCreditEntry.ManualGrowthCreditEntry',
}

export interface IGrowthCreditEntry extends IBaseAttributes {
  _cls: EGrowthCreditEntryClass;
  credits: number;
  description: string;
  account: string;
  date_entered: string;
}

export interface IRedemptionGrowthCreditEntry extends IGrowthCreditEntry {
  _cls:
    | EGrowthCreditEntryClass.Redemption
    | EGrowthCreditEntryClass.MediaRedemption
    | EGrowthCreditEntryClass.InvoiceRedemption;
  growth_credit_rate: string;
  user: string;
}

export interface IMediaRedemptionGrowthCreditEntry extends IRedemptionGrowthCreditEntry {
  _cls: EGrowthCreditEntryClass.MediaRedemption;
  project: string;
}

export interface IInvoiceRedemptionGrowthCreditEntry extends IRedemptionGrowthCreditEntry {
  _cls: EGrowthCreditEntryClass.InvoiceRedemption;
}

export type TGrowthCreditEntry =
  | IGrowthCreditEntry
  | IRedemptionGrowthCreditEntry
  | IMediaRedemptionGrowthCreditEntry
  | IInvoiceRedemptionGrowthCreditEntry;

export class GrowthCreditEntry extends Model<TGrowthCreditEntry> {
  public readonly className = 'GrowthCreditEntry';

  public get constraints(): TConstraints<IGrowthCreditEntry> {
    return {};
  }

  public get isRedemption(): boolean {
    const cls = this.get('_cls');
    return (
      cls === EGrowthCreditEntryClass.Redemption ||
      cls === EGrowthCreditEntryClass.MediaRedemption ||
      cls === EGrowthCreditEntryClass.InvoiceRedemption
    );
  }

  public get isMediaRedemption(): boolean {
    return this.get('_cls') === EGrowthCreditEntryClass.MediaRedemption;
  }

  public get isInvoiceRedemption(): boolean {
    return this.get('_cls') === EGrowthCreditEntryClass.InvoiceRedemption;
  }
}

export interface IExportRedemptionsParams {
  email: string;
  redemption_type?: 'media_balance' | 'invoice_payment';
  start_date?: string;
  end_date?: string;
}

export class GrowthCreditEntries extends Collection<GrowthCreditEntry> {
  public getClassName(): string {
    return 'growth_credit_entries';
  }

  public override url(): string {
    return `${this.getHostname()}growth-credits-entry/`;
  }

  public getModel(attributes: Partial<TGrowthCreditEntry>): GrowthCreditEntry {
    return new GrowthCreditEntry(attributes);
  }

  public async exportRedemptions(params: IExportRedemptionsParams): Promise<IRachisMessage> {
    const response = await wretch<IRachisMessage>(
      `${this.getHostname()}growth-credit-redemption/export`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params),
      },
    );

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
